import { messageBoxSuccess, messageBoxError } from './message-box';
import FormValidation from './form-validation';

export default class LayoutFormMaterials {
  private elScope: HTMLElement;

  private el: {
    moduleMaterials: HTMLElement;
    itemsContent: HTMLElement;
    btsOpen: NodeListOf<HTMLButtonElement>;

    form: HTMLFormElement;
    inputs: NodeListOf<HTMLInputElement>;
    textArea: HTMLTextAreaElement;
    inputCompany: HTMLInputElement;
    checkTerms: HTMLInputElement;
    buttonTabs: NodeListOf<HTMLButtonElement>;
    tabColumn: HTMLElement;

    checkAccount: HTMLInputElement;
    inputsAccount: NodeListOf<HTMLInputElement>;

    inputLookbook: HTMLInputElement;
    lookbookImage: HTMLElement;

    btSubmit: HTMLButtonElement;

    btHome: HTMLElement;
  };

  private className = {
    hasItems: 'has-items',
    isAccountRequired: 'is-account-required',
  };

  private status = {
    SENDING: 'sending',
    ERROR: 'error',
    TANKYOU_MESSAGE: 'ty',
  };

  public idsAdded: number[];

  private STORAGE_NAME = 'val-materials-v1.2.0';

  private endpoint = '/api/v1/forms/materials';

  private hasItems = false;

  private formValidation: FormValidation;

  private apiBaseUrl: string | undefined;

  init() {
    this.cacheElements();
    this.bindings();
  }

  private cacheElements() {
    this.elScope = document.querySelector('#popup-materials-form') as HTMLElement;
    this.el = {
      moduleMaterials: document.querySelector('.js-o-mtr') as HTMLElement,
      btsOpen: document.querySelectorAll('.js-o-mtr button[data-target="popup-materials-form"]'),
      itemsContent: document.querySelector('#js-l-form-m__items-list') as HTMLElement,

      form: this.elScope?.querySelector('form') as HTMLFormElement,

      inputs: this.elScope?.querySelectorAll('form input') as NodeListOf<HTMLInputElement>,
      textArea: this.elScope?.querySelector('form textarea') as HTMLTextAreaElement,
      inputCompany: this.elScope?.querySelector('form input[name="company"]') as HTMLInputElement,
      checkTerms: this.elScope?.querySelector('#popup-materials-form__terms') as HTMLInputElement,
      buttonTabs: this.elScope?.querySelectorAll('.js-form-tab-switch') as NodeListOf<HTMLButtonElement>,
      tabColumn: this.elScope?.querySelector('[data-key="company"]') as HTMLElement,
      btSubmit: this.elScope?.querySelector('button[type="submit"]') as HTMLButtonElement,

      checkAccount: this.elScope?.querySelector('#popup-materials-form__account') as HTMLInputElement,
      inputsAccount: this.elScope?.querySelectorAll('.l-form-m__account-i') as NodeListOf<HTMLInputElement>,

      inputLookbook: this.elScope?.querySelector('#materials_lookbook') as HTMLInputElement,
      lookbookImage: this.elScope?.querySelector('.js-l-form-m__extra-i-bg') as HTMLElement,

      btHome: this.elScope?.querySelector('.js-l-form-materials__bt-home') as HTMLFormElement,
    };

    this.endpoint = this.el.form?.action;
    this.apiBaseUrl = document.body.dataset.apiBaseUrl;

    this.getInitialIds();
    this.reportIdsUpdated();
    this.sanitizeIds();
    this.updateUiMaterial();
  }

  private bindings() {
    const { el } = this;

    document.addEventListener('materials:add', (e: any) => this.addId(e.detail.id));
    document.addEventListener('materials:remove', (e: any) => this.removeId(e.detail.id));
    document.addEventListener('materials:ui-updated', () => this.updateUiMaterial());

    el.btHome?.addEventListener('click', () => this.goHome());
    el.btsOpen?.forEach((bt) => bt.addEventListener('click', () => this.goHome()));
    if (el.buttonTabs?.length > 0) {
      el.buttonTabs[0].addEventListener('click', (e) => this.toggleInputCompany(e, false));
      el.buttonTabs[1].addEventListener('click', (e) => this.toggleInputCompany(e, true));
    }

    if(el.inputLookbook) {
      el.inputLookbook.addEventListener('change', () => this.toggleLookbook());
    }

    if (el.checkAccount) {
      el.checkAccount.addEventListener('change', () => this.toggleInputsAccount(el.checkAccount.checked));
    }

    this.formValidation = new FormValidation(
      el.form,
      this.handlerFormSubmit.bind(this),
      this.handlerError.bind(this)
    );
    this.formValidation.init();
  }

  private toggleLookbook() {
    const { el } = this;
    el.lookbookImage.classList.toggle('is-active');
  }

  private getInitialIds() {
    const { STORAGE_NAME } = this;
    const ids = window.sessionStorage.getItem(STORAGE_NAME);
    this.idsAdded = [];
    try {
      this.idsAdded = ids ? JSON.parse(ids) : [];
    } catch(e) {
      console.error(e);
      this.updateIds();
    }
  }

  private updateIds() {
    const { STORAGE_NAME } = this;
    window.sessionStorage.setItem(STORAGE_NAME, JSON.stringify(this.idsAdded));
  }

  private addId(id: number) {
    const isIDAdded = this.idsAdded.includes(id);
    if (!isIDAdded) {
      this.idsAdded.push(id);
      this.updateIds();
      this.reportIdsUpdated();
    }
  }

  private removeId(id: number) {
    const index = this.idsAdded.indexOf(Number(id));
    if (index > -1) {
      this.idsAdded.splice(index, 1);
      this.updateIds();
      this.reportIdsUpdated();
    }
  }

  private reportIdsUpdated() {
    document.dispatchEvent(
      new CustomEvent('form-materials:ids-updated', {
        detail: { ids: this.idsAdded },
      } as any)
    );
  }

  // Double check if the IDs from the local storage are still valid
  private sanitizeIds() {
    const { el } = this;
    const hasPostCards = el.moduleMaterials?.querySelectorAll('.js-m-post-card.is-added').length > 0;

    if (hasPostCards) {
      let validIdsAdded: any[] = [];

      this.idsAdded.forEach((idAdded: any) => {
        const elCard = el.moduleMaterials?.querySelector(`.js-m-post-card.is-added[data-id="${idAdded}"]`);
        if (elCard) {
          validIdsAdded.push(idAdded);
        }
      });

      if (validIdsAdded.length > 0) {
        this.idsAdded = validIdsAdded;
        this.updateIds();
        this.reportIdsUpdated();
      }
    }
  }

  private updateUiMaterial() {
    const { elScope, el, className, idsAdded } = this;

    let htmlCards = '';

    idsAdded.forEach((idAdded: any) => {
      const elCard = el.moduleMaterials?.querySelector(`.js-m-post-card.is-added[data-id="${idAdded}"]`);

      if (elCard) {
        const title = elCard.querySelector('.js-m-post-card__title')?.innerHTML || 'title not found';
        const elImage = elCard.querySelector('.js-m-post-card__image') as HTMLImageElement;
        const srcImage = elImage ? elImage.src : '';
        htmlCards += `
          <div class='l-form-materials__item'>
            <div class='l-form-materials__item-content'>
              <div class='l-form-materials__item-bg' style='background-image:url(${srcImage})'></div>
              <h4 class='m-post-card__title'>${title}</h4>
            </div>
          </div>
        `;
      }
    });

    this.hasItems = htmlCards !== '';

    if (el.itemsContent) {
      el.itemsContent.innerHTML = htmlCards;
    }
    if (el.btSubmit) {
      el.btSubmit.disabled = !this.hasItems;
    }
    if (elScope) {
      elScope.classList.toggle(className.hasItems, this.hasItems);
    }
  }

  private async handlerFormSubmit() {
    if (!this.hasItems || this.getIsLocked() || this.idsAdded.length <= 0) return;

    this.setIsLocked();
    const result = await this.submitData();
    if (result.success) {
      if (result.response && result.response === 'error') {
        this.setError();
      } else {
        this.cleanInputs();
        this.setTy();
      }
    } else if (result.response !== null) {
      this.setError();
    }
    else {
      this.setIsLocked(false);
    }
  }

  private handlerError() {
    const elError = this.el.form.querySelector('.has-error');
    if (elError) {
      const top = elError.getBoundingClientRect().top + this.elScope.scrollTop - 50;
      this.elScope.scrollTo({top});
      (elError as HTMLElement).focus();
    }
  }

  private toggleInputsAccount(isActive: boolean) {
    const { el, elScope, className } = this;

    el.inputsAccount.forEach((elInput) => {
      if (isActive) {
        elInput.setAttribute('required', '');
      } else {
        elInput.removeAttribute('required');
        elInput.classList.remove('has-error');
        elInput.parentElement?.querySelector('.error-message')?.classList.add('is-hidden');
      }
    });
    elScope.classList.toggle(className.isAccountRequired, isActive);
    el.inputsAccount[0].focus();
  }

  private toggleInputCompany(e: MouseEvent, isActive: boolean) {
    const { el } = this;
    const tabColumnNeighbor = el.tabColumn.nextElementSibling;
    el.buttonTabs.forEach( (buttonTab) => buttonTab.classList.remove('is--active'));
    (e.target as HTMLElement).classList.add('is--active');

    el.inputCompany.value = '';
    if (isActive) {
      el.inputCompany.setAttribute('required', '');
      el.tabColumn.classList.remove('hidden');
      tabColumnNeighbor?.classList.add('col-md-offset-2');
      el.inputCompany.focus();
    } else {
      el.inputCompany.removeAttribute('required');
      el.tabColumn.classList.add('hidden');
      tabColumnNeighbor?.classList.remove('col-md-offset-2');
      el.inputs[1].focus();
    }
  }

  private getIsLocked() {
    const { elScope, status } = this;
    return elScope.dataset.status === status.SENDING;
  }

  private setIsLocked(isLocked: boolean = true) {
    const { elScope, status } = this;
    elScope.dataset.status = isLocked ? status.SENDING : '';
  }

  private setError() {
    const { elScope, status } = this;
    elScope.dataset.status = status.ERROR;
    this.scrollToTop();
  }

  private setTy() {
    const { elScope, status } = this;
    elScope.dataset.status = status.TANKYOU_MESSAGE;
    this.scrollToTop();
  }

  private cleanInputs() {
    const { el } = this;
    el.inputs.forEach((elInput: HTMLInputElement) => {
      elInput.value = '';
    });
    el.textArea.value = '';

    this.idsAdded = [];
    this.reportIdsUpdated();
  }

  private goHome() {
    this.setIsLocked(false);
  }

  private scrollToTop() {
    this.elScope.scrollTo({
      top: 0,
      left: 0,
    });
    setTimeout(() => {
      this.el.inputs[1].focus();
    }, 200);
  }

  async createAccount(createAccount: boolean) {
    let success = true;
    if(createAccount) {
      success = false;
      const data = new FormData();

      // @ts-ignore
      data.append(window.csrfTokenName, window.csrfTokenValue);
      data.append('action', 'users/save-user');

      let salutation = this.el.form.querySelector('select[name="salutation"]');
      if (salutation instanceof HTMLSelectElement) {
        data.append('fields[salutation]', salutation.value);
        data.append('address[fields][addressTitle]', salutation.value);
      }

      let fullName = '';
      let firstName = this.el.form.querySelector('input[name="firstName"]');
      if (firstName instanceof HTMLInputElement) {
        data.append('firstName', firstName.value);
        data.append('fields[firstName]', firstName.value);
        data.append('address[firstName]', firstName.value);
        fullName = firstName.value;
      }

      let lastName = this.el.form.querySelector('input[name="lastName"]');
      if (lastName instanceof HTMLInputElement) {
        data.append('lastName', lastName.value);
        data.append('fields[lastName]', lastName.value);
        data.append('address[lastName]', lastName.value);
        fullName = fullName + ' ' + lastName.value;
      }

      data.append('fullName', fullName);
      data.append('fields[fullName]', fullName);
      data.append('address[fullName]', fullName);

      let email = this.el.form.querySelector('input[name="email"]');
      if (email instanceof HTMLInputElement) {
        data.append('email', email.value);
        data.append('fields[email]', email.value);
      }

      let phone = this.el.form.querySelector('input[name="phoneNumber"]');
      if (phone instanceof HTMLInputElement) {
        data.append('fields[phone]', phone.value);
        data.append('address[fields][phoneNumber]', phone.value);
      }

      let organization = this.el.form.querySelector('input[name="organization"]');
      let company = this.el.form.querySelector('input[name="company"]');
      if (organization instanceof HTMLInputElement) {
        data.append('fields[companyName]', organization.value);
        data.append('address[organization]', organization.value);
      }
      else if (company instanceof HTMLInputElement) {
        data.append('fields[companyName]', company.value);
        data.append('address[organization]', company.value);
      }

      let postalCode = this.el.form.querySelector('input[name="postalCode"]');
      if (postalCode instanceof HTMLInputElement) {
        data.append('address[postalCode]', postalCode.value);
      }

      let locality = this.el.form.querySelector('input[name="locality"]');
      if (locality instanceof HTMLInputElement) {
        data.append('address[locality]', locality.value);
      }

      let street = this.el.form.querySelector('input[name="street"]');
      if (street instanceof HTMLInputElement) {
        data.append('address[addressLine1]', street.value);
      }

      let notes = this.el.form.querySelector('input[name="addressNotes"]');
      if (notes instanceof HTMLInputElement) {
        data.append('address[fields][addressNotes]', notes.value);
      }

      let country = this.el.form.querySelector('select[name="country"]');
      if (country instanceof HTMLSelectElement) {
        data.append('address[countryCode]', country.value);
      }

      let password = this.el.form.querySelector('input[name="password"]');
      if (password instanceof HTMLInputElement) {
        data.append('password', password.value);
      }

      await fetch(this.apiBaseUrl!, {
        method: 'post',
        headers: {
          Accept: 'application/json',
        },
        body: data,
      })
      .then((res) => res.json())
      .then((res) => {
        if (res.id && res.id != "") {
          success = true;
          messageBoxSuccess({
            contentDe: 'Account erfolgreich angelegt. Bitte überprüfe dein E-Mail-Postfach.',
            contentEn: 'Account successfully created. Please check your email inbox.',
          });

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'sign_up',
            user: {
              email: data.get('email')
            }
          });
        } else {
          throw new Error(JSON.stringify(res.errors));
        }
      })
      .catch((error) => {
        console.error(error);
        messageBoxError({
          contentDe: 'Account konnte nicht angelegt werden',
          contentEn: 'Account could not be created',
        });
      });
    }
    return success;
  }

  private async submitLookbookDownload() {
    const { el } = this;

    let success = true;
    //let response = 'error';

    if (el.inputLookbook && el.inputLookbook.checked) {
      success = false;
      const fileId = el.inputLookbook.dataset.fileId ?? '';
      const lang = el.inputLookbook.dataset.fileLang ?? '';
      const userMail = (this.el.form?.querySelector('input[name="email"]') as HTMLInputElement).value;
      const valBase64 = window.btoa((this.el.form?.querySelector('input[name="email"]') as HTMLInputElement).value || '');
      const apiBaseUrl = document.body.dataset.apiBaseUrl;

      const downloadEndpoint = `${apiBaseUrl}api/v1/download/id/${fileId}/lang/${lang}/flex/${valBase64}`;

      await fetch(downloadEndpoint, {
        method: 'GET',
      })
      .then((res) => {
        if (res.headers.get('Content-Disposition') !== null && res.status == 200) {
          return res.blob().then((blob) => {
            const file = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            const filename = el.inputLookbook.dataset.fileName ?? file.substr(file.lastIndexOf('/') + 1);
            a.href = file;
            a.download = filename;
            document.body.appendChild(a);
            a.style.display = 'none';
            a.click();
            document.body.removeChild(a);
            success = true;
            return {ok: true};
          });
        }
        return res.json();
      })
      .then(data => {
        if (data.success == true || data.success === 'true') {
          success = true;
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ action: null });
          window.dataLayer.push({
            event: 'generate_lead',
            lead: {
              type: 'form_download',
              email: userMail,
              content_type: 'file',
              file_title: data.hasOwnProperty('file_title') ? data.file_title : 'unknown',
              new_lead: data.hasOwnProperty('is_first_contact') && data.is_first_contact ? 'Yes' : 'No'
            }
          });
        }
      })
      .catch(error => {
        console.warn(error);
        //response = 'error undefined';

        messageBoxError({
          contentDe: 'Beim Download ist ein Fehler aufgetreten',
          contentEn: 'An error occurred during download',
        });
      });
    }
    return success;
  }

  private async submitData(): Promise<{ success: boolean; response: any }> {
    const { endpoint, el } = this;
    let success = false;
    let response = 'error';

    const data = new FormData(el.form);
    data.set('material', this.idsAdded.join(';'));

    this.elScope.classList.add('loading');

    if (el.checkTerms.checked === true) {
      data.set('terms_accepted', 'true');
    }

    let createAccount = false;
    const createAccountCheckbox = this.el.form?.querySelector('input[name="create_account"]') as HTMLInputElement;

    if (createAccountCheckbox?.checked) {
      const password = this.el.form?.querySelector('input[name="password"]') as HTMLInputElement;
      const passwordRepeat = this.el.form?.querySelector('input[name="password-repeat"]') as HTMLInputElement;

      if (password.value === passwordRepeat.value && password.value !== '') {
        createAccount = true;
      } else {
        messageBoxError({
          contentDe: 'Es ist ein Fehler aufgetreten. Bitte überprüfe Deine Passwörter.',
          contentEn: 'An error appeared. Please check your passwords.',
        });

        password.classList.add('has-error');
        passwordRepeat.classList.add('has-error');
        password.addEventListener('keydown', () => password.classList.remove('has-error'));
        passwordRepeat.addEventListener('keydown', () => passwordRepeat.classList.remove('has-error'));

        this.elScope.classList.remove('loading');

        return { success: false, response: null }; // temp i guess
      }

      // Create the new account first
      if(createAccount && createAccountCheckbox?.checked) {
        const createAccountSuccess = await this.createAccount(true);
        if (createAccountSuccess == false) {
          this.elScope.classList.remove('loading');
          return { success: false, response: null }; // temp i guess
        }
      }
    }

    if (this.el.form?.reportValidity()) {
      const materialMail = fetch(endpoint, {
        method: 'POST',
        body: data,
      })
        .then((res) => res.json())
        .then((res) => {
          if (res.success == true || res.success === 'true') {
            success = true;
            response = 'success';
            messageBoxSuccess({
              contentDe: 'E-Mail wurde erfolgreich gesendet.',
              contentEn: 'Successfully sent e-mail.',
            });
            // console.log(data.get('email'));
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ action: null });
            window.dataLayer.push({
              event: 'generate_lead',
              lead: {
                type: 'form_samples',
                email: data.get('email'),
                new_lead: res.hasOwnProperty('is_first_contact') && res.is_first_contact ? 'Yes' : 'No',
              }
            });
          }
        })
        .catch((error) => {
          console.warn(error);
          response = 'error undefined';
          messageBoxError({
            contentDe: 'Es ist ein Fehler aufgetreten. E-Mail konnte nicht gesendet werden.',
            contentEn: 'An error appeared. Could not send e-mail.',
          });
        });


      await materialMail;
      const waitDownload = this.submitLookbookDownload();
      const downloadSuccess = await waitDownload;

      if(!success) response = 'error undefined';
    }

    return { success , response };
  }
}
